/**
 * Utility functions for date formatting and manipulation
 */
import { format } from 'date-fns-tz';

/**
 * Format a date string to a more readable format
 * @param dateString - The date string to format (YYYY-MM-DD)
 * @returns Formatted date string (DD MMM YYYY)
 */
export const formatDate = (dateString: string | null): string => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
};

/**
 * Get the first day of the current month
 * @returns Date string in YYYY-MM-DD format
 */
export const getFirstDayOfMonth = (): string => {
  const date = new Date();
  date.setDate(1);
  return date.toISOString().split('T')[0];
};

/**
 * Get the current date
 * @returns Date string in YYYY-MM-DD format
 */
export const getCurrentDate = (): string => {
  return new Date().toISOString().split('T')[0];
};

/**
 * Check if a date is valid
 * @param dateString - The date string to check
 * @returns Boolean indicating if the date is valid
 */
export const isValidDate = (dateString: string): boolean => {
  const date = new Date(dateString);
  return !isNaN(date.getTime());
};

/**
 * Format a date range as a string
 * @param startDate - Start date string
 * @param endDate - End date string
 * @returns Formatted date range string
 */
export const formatDateRange = (startDate: string | null, endDate: string | null): string => {
  if (!startDate || !endDate) return '';
  return `${formatDate(startDate)} - ${formatDate(endDate)}`;
};

/**
 * Format a datetime string to IST timezone in dd-MM-yyyy HH:mm:ss format (24-hour format)
 * @param dateString - The ISO datetime string to format (can include timezone)
 * @returns Formatted datetime string in IST timezone (dd-MM-yyyy HH:mm:ss) or 'N/A' if invalid
 * @example formatDateTimeIST('2026-01-13T09:31:39+00:00') returns '13-01-2026 15:01:39'
 */
export const formatDateTimeIST = (dateString: string | null): string => {
  if (!dateString) return 'N/A';
  try {
    // Parse the ISO date string (handles both UTC and timezone-aware strings)
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn(`[formatDateTimeIST] Invalid date string: ${dateString}`);
      return dateString;
    }
    
    // Convert to IST timezone and format in 24-hour format
    // HH = 24-hour format (00-23), hh = 12-hour format (01-12)
    const IST_TIMEZONE = 'Asia/Kolkata';
    const formatted = format(date, 'dd-MM-yyyy HH:mm:ss', { timeZone: IST_TIMEZONE });
    return formatted;
  } catch (error) {
    console.error('[formatDateTimeIST] Error formatting datetime in IST:', error, 'Input:', dateString);
    return dateString;
  }
}; 